import React, { useState } from 'react';
import { Layout, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import AddDeviceModal from '../components/DeviceManagement/AddDeviceModal';
import SensorTable from '../components/DeviceManagement/SensorTable';
import { useData } from '../contexts/DataContext';

const { Content } = Layout;

const DeviceManagement = () => {
  const { 
    devices, 
    loading, 
    error,
    addDevice,
    updateDevice,
    deleteDevice
  } = useData();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  const handleAddDevice = () => {
    setEditingDevice(null);
    setIsModalVisible(true);
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingDevice(null);
  };

  const handleFinishAddDevice = async (deviceInfo) => {
    try {
      if (editingDevice) {
        await updateDevice(editingDevice.name, deviceInfo);
        message.success('Device updated successfully');
      } else {
        await addDevice(deviceInfo);
        message.success('Device added successfully');
      }
      setIsModalVisible(false);
    } catch (error) {
      message.error('Failed to save device. Please try again.');
    }
  };

  const handleDeleteDevice = async (deviceName) => {
    try {
      await deleteDevice(deviceName);
      message.success('Device deleted successfully');
    } catch (error) {
      message.error('Failed to delete device. Please try again.');
    }
  };

  const handleAction = (action, device) => {
    switch (action) {
      case 'configure':
        message.info(`Configuring device: ${device.name}`);
        // Implement configuration logic here
        break;
      case 'diagnose':
        message.info(`Diagnosing device: ${device.name}`);
        // Implement diagnosis logic here
        break;
      case 'activate':
        message.info(`Activating device: ${device.name}`);
        // Implement activation logic here
        break;
      case 'deactivate':
        message.info(`Deactivating device: ${device.name}`);
        // Implement deactivation logic here
        break;
      case 'delete':
        handleDeleteDevice(device.name);
        break;
      default:
        message.warn(`Unknown action: ${action}`);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  // Transform devices data to match SensorTable expected format
  const sensorsData = devices.map(device => ({
    id: device.name,
    name: device.name,
    type: device.attributes?.TypeofMeasurements 
      ? device.attributes.TypeofMeasurements.split(',')[0] 
      : (device.name.includes('Radar') ? 'Radar' : 'Unknown'),
    status: 'Unknown', // Status is not provided in the current data structure
    lastPing: 'N/A', // Last ping is not provided in the current data structure
    batteryLevel: 0, // Battery level is not provided in the current data structure
    location: device.attributes?.Location || 'N/A',
    latitude: device.attributes?.latitude || 'N/A',
    longitude: device.attributes?.longitude || 'N/A',
    measurements: device.attributes?.TypeofMeasurements || 'N/A',
    unit1: device.attributes?.unit1 || 'N/A',
    unit2: device.attributes?.unit2 || 'N/A',
  }));

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow flex flex-col">
        <Content className="bg-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-lg font-semibold">IoT Device Management</h1>
            <Button className="flex items-center" icon={<PlusOutlined className="mr-2" />} onClick={handleAddDevice}>
              Add Device
            </Button>
          </div>
          <SensorTable 
            sensors={sensorsData} 
            onAction={handleAction}
          />
        </Content>
      </div>
      <AddDeviceModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onFinish={handleFinishAddDevice}
        editingDevice={editingDevice}
      />
    </div>
  );
};

export default DeviceManagement;